/// <amd-module name="Feed/Legacy/Components/PackageUpstreamLocatorCommand" />
import * as React from "react";

import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { IPageData, IVssPageService } from "VSS/Platform/Page";

import { CiConstants, FwLinks } from "Feed/Common/Constants/Constants";
import { Feed } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { ExternalLink } from "Feed/Legacy/Components/ExternalLink";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import * as OrganizationHelper from "Feed/Legacy/Helpers/OrganizationHelper";
import { IPackageProtocol } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { CopyableTextField } from "Feed/Legacy/Protocols/Components/CopyableTextField";
import { MinimalPackageVersion } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export interface IPackageUpstreamLocatorCommandProps {
    pageContext: IVssPageContext;

    /**
     * the currently selected feed
     */
    feed: Feed;

    /**
     * the currently selected packageVersion
     */
    packageVersion: MinimalPackageVersion;

    /**
     * the currently selected protocol
     */
    protocol: IPackageProtocol;
}

export interface IPackageUpstreamLocatorCommandState {
    /**
     * the locator string that will be displayed on the text field
     */
    locator: string;
}

export class PackageUpstreamLocatorCommand extends React.Component<
    IPackageUpstreamLocatorCommandProps,
    IPackageUpstreamLocatorCommandState
> {
    private _collectionContext: { id: string; name: string };
    private _webPageDataService: HubWebPageDataService;

    constructor(props: IPackageUpstreamLocatorCommandProps) {
        super(props);

        this.state = {
            locator: ""
        };

        const pageData: IPageData = this.props.pageContext.getService<IVssPageService>("IVssPageService").getData();
        this._collectionContext = { id: pageData.hostId, name: pageData.hostName };

        this._webPageDataService = this.props.pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");
    }

    public componentDidMount(): void {
        this._getLocatorAsync().then(
            locator => {
                this.setState({
                    locator
                });
            },
            error => {
                // Most likely the org token was null or incorrect
                // Since locator is not set, the component won't render
            }
        );
    }

    public render(): JSX.Element {
        if (this._webPageDataService.isOrgAadBacked() === false) {
            return null;
        }

        return (
            this.state.locator !== "" && (
                <div className="package-upstream-locator-command">
                    <FormatComponent
                        // fmt: "Already connected to another feed? Copy this feed locator and add an {0} to your feed, then run the install command above. {1}"
                        // {0}: "upstream source"
                        // {1}: "Here's how"
                        format={PackageResources.UpstreamLocatorCommand_Description}
                    >
                        <span className="italic-text">{PackageResources.upstream_source}</span>
                        <ExternalLink
                            pageContext={this.props.pageContext}
                            href={FwLinks.UpstreamLocatorCommandHeresHow}
                        >
                            {PackageResources.Heres_how}
                        </ExternalLink>
                    </FormatComponent>

                    <div className="locator-text-field">
                        <CopyableTextField
                            pageContext={this.props.pageContext}
                            text={this.state.locator}
                            buttonAriaLabel={PackageResources.Copy_upstream_source_locator}
                            textFieldAriaLabel={PackageResources.Upstream_source_locator}
                            telemetryProperties={{
                                commandName: CiConstants.PackageUpstreamLocatorCommand,
                                feedName: this.props.feed.name,
                                packageName: this.props.packageVersion.id,
                                packageVersion: this.props.packageVersion.version,
                                protocol: this.props.protocol.name
                            }}
                        />
                    </div>
                </div>
            )
        );
    }

    private async _getLocatorAsync(): Promise<string> {
        // Local package
        if (!this.props.packageVersion.directUpstreamSourceId) {
            return OrganizationHelper.getUpstreamLocator(
                this._collectionContext.name,
                this.props.feed.name,
                this.props.feed.view ? this.props.feed.view.name : "Local",
                this.props.feed.project?.name
            );
        }

        const upstreamSourceIndex = this.props.feed.upstreamSources.findIndex(
            s => s.id === this.props.packageVersion.directUpstreamSourceId
        );
        if (upstreamSourceIndex >= 0) {
            const upstreamSource = this.props.feed.upstreamSources[upstreamSourceIndex];
            // Prefer showing the user a friendly name formatted location,
            // but fall back to ID format if displayLocation couldn't be computed
            return upstreamSource.displayLocation || upstreamSource.location;
        }

        // We couldn't find a locator
        return "";
    }
}
